/**
 * Offline Storage Service using IndexedDB
 * Provides persistent storage for offline data with better performance than localStorage
 */

interface OfflineData {
  id: string;
  type: 'customer' | 'booking' | 'location' | 'contact' | 'reminder' | 'certificate';
  data: any;
  timestamp: number;
  synced: boolean;
}

class OfflineStorageService {
  private dbName = 'rapidcert_offline_db';
  private dbVersion = 1;
  private db: IDBDatabase | null = null;
  private initPromise: Promise<void> | null = null;

  constructor() {
    this.init();
  }

  private async init(): Promise<void> {
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = new Promise((resolve, reject) => {
      if (typeof indexedDB === 'undefined') {
        console.warn('IndexedDB not supported, falling back to localStorage');
        resolve();
        return;
      }

      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => {
        console.error('Failed to open IndexedDB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        console.log('✅ IndexedDB initialized for offline storage');
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Create object stores for different data types
        if (!db.objectStoreNames.contains('customers')) {
          db.createObjectStore('customers', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('bookings')) {
          db.createObjectStore('bookings', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('locations')) {
          db.createObjectStore('locations', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('contacts')) {
          db.createObjectStore('contacts', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('mutations')) {
          const mutationStore = db.createObjectStore('mutations', { keyPath: 'id', autoIncrement: true });
          mutationStore.createIndex('timestamp', 'timestamp', { unique: false });
          mutationStore.createIndex('synced', 'synced', { unique: false });
          mutationStore.createIndex('type', 'type', { unique: false });
        }
        if (!db.objectStoreNames.contains('cache')) {
          const cacheStore = db.createObjectStore('cache', { keyPath: 'key' });
          cacheStore.createIndex('timestamp', 'timestamp', { unique: false });
        }
      };
    });

    return this.initPromise;
  }

  private async ensureDB(): Promise<IDBDatabase> {
    await this.init();
    if (!this.db) {
      throw new Error('IndexedDB not available');
    }
    return this.db;
  }

  /** Clear cached CRM rows in IndexedDB (used after offline sync instead of re-downloading entire tables). */
  async clearEntityStore(type: 'customers' | 'bookings'): Promise<void> {
    try {
      if (typeof indexedDB === 'undefined') {
        try {
          localStorage.removeItem(`offline_${type}`);
        } catch {
          /* ignore */
        }
        return;
      }
      const db = await this.ensureDB();
      const transaction = db.transaction([type], 'readwrite');
      const store = transaction.objectStore(type);
      await new Promise<void>((resolve, reject) => {
        const req = store.clear();
        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error);
      });
    } catch (error) {
      console.error(`Failed to clear IndexedDB store ${type}:`, error);
    }
  }

  // Store data for offline access
  async storeData(type: 'customers' | 'bookings' | 'locations' | 'contacts', data: any[]): Promise<void> {
    try {
      const db = await this.ensureDB();
      const transaction = db.transaction([type], 'readwrite');
      const store = transaction.objectStore(type);

      // Clear existing data
      await new Promise<void>((resolve, reject) => {
        const clearRequest = store.clear();
        clearRequest.onsuccess = () => resolve();
        clearRequest.onerror = () => reject(clearRequest.error);
      });

      // Store new data
      for (const item of data) {
        await new Promise<void>((resolve, reject) => {
          const addRequest = store.put(item);
          addRequest.onsuccess = () => resolve();
          addRequest.onerror = () => reject(addRequest.error);
        });
      }

      console.log(`💾 Stored ${data.length} ${type} in IndexedDB`);
    } catch (error) {
      console.error(`Failed to store ${type} in IndexedDB:`, error);
      // Fallback to localStorage if IndexedDB fails
      this.fallbackToLocalStorage(type, data);
    }
  }

  // Retrieve data from offline storage
  async getData<T>(type: 'customers' | 'bookings' | 'locations' | 'contacts'): Promise<T[]> {
    try {
      const db = await this.ensureDB();
      const transaction = db.transaction([type], 'readonly');
      const store = transaction.objectStore(type);

      return new Promise<T[]>((resolve, reject) => {
        const request = store.getAll();
        request.onsuccess = () => resolve(request.result || []);
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error(`Failed to get ${type} from IndexedDB:`, error);
      // Fallback to localStorage
      return this.getFromLocalStorage<T>(type);
    }
  }

  // Queue a mutation for later sync
  async queueMutation(mutation: Omit<OfflineData, 'id' | 'synced'>): Promise<string> {
    try {
      const db = await this.ensureDB();
      const transaction = db.transaction(['mutations'], 'readwrite');
      const store = transaction.objectStore('mutations');

      const mutationData: OfflineData = {
        ...mutation,
        id: `${mutation.type}_${mutation.timestamp}_${Math.random()}`,
        synced: false,
      };

      return new Promise<string>((resolve, reject) => {
        const request = store.add(mutationData);
        request.onsuccess = () => {
          console.log(`📝 Queued mutation: ${mutation.type}`, mutationData.id);
          resolve(mutationData.id);
        };
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Failed to queue mutation:', error);
      // Fallback to localStorage
      return this.queueMutationInLocalStorage(mutation);
    }
  }

  // Get all pending mutations
  async getPendingMutations(): Promise<OfflineData[]> {
    try {
      const db = await this.ensureDB();
      const transaction = db.transaction(['mutations'], 'readonly');
      const store = transaction.objectStore('mutations');

      return new Promise<OfflineData[]>((resolve, reject) => {
        const request = store.getAll();

        request.onsuccess = () => {
          const allMutations = request.result || [];
          // Filter for unsynced mutations only
          const unsyncedMutations = allMutations.filter((m: OfflineData) => !m.synced);
          // Sort by timestamp (oldest first)
          unsyncedMutations.sort((a, b) => a.timestamp - b.timestamp);
          resolve(unsyncedMutations);
        };

        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Failed to get pending mutations:', error);
      return this.getPendingMutationsFromLocalStorage();
    }
  }

  // Mark mutation as synced
  async markMutationSynced(mutationId: string): Promise<void> {
    try {
      const db = await this.ensureDB();
      const transaction = db.transaction(['mutations'], 'readwrite');
      const store = transaction.objectStore('mutations');

      return new Promise<void>((resolve, reject) => {
        const getRequest = store.get(mutationId);
        getRequest.onsuccess = () => {
          const mutation = getRequest.result;
          if (mutation) {
            mutation.synced = true;
            const putRequest = store.put(mutation);
            putRequest.onsuccess = () => {
              console.log(`✅ Marked mutation as synced: ${mutationId}`);
              resolve();
            };
            putRequest.onerror = () => reject(putRequest.error);
          } else {
            resolve();
          }
        };
        getRequest.onerror = () => reject(getRequest.error);
      });
    } catch (error) {
      console.error('Failed to mark mutation as synced:', error);
      this.markMutationSyncedInLocalStorage(mutationId);
    }
  }

  // Clear synced mutations (cleanup)
  async clearSyncedMutations(olderThan: number = 7 * 24 * 60 * 60 * 1000): Promise<void> {
    try {
      const db = await this.ensureDB();
      const transaction = db.transaction(['mutations'], 'readwrite');
      const store = transaction.objectStore('mutations');
      const index = store.index('synced');

      return new Promise<void>((resolve, reject) => {
        const request = index.getAll(true); // Get all synced mutations
        request.onsuccess = () => {
          const mutations = request.result || [];
          const cutoff = Date.now() - olderThan;
          const toDelete = mutations.filter(m => m.timestamp < cutoff);

          let deleted = 0;
          if (toDelete.length === 0) {
            resolve();
            return;
          }

          toDelete.forEach(mutation => {
            const deleteRequest = store.delete(mutation.id);
            deleteRequest.onsuccess = () => {
              deleted++;
              if (deleted === toDelete.length) {
                console.log(`🗑️ Cleared ${deleted} old synced mutations`);
                resolve();
              }
            };
            deleteRequest.onerror = () => reject(deleteRequest.error);
          });
        };
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Failed to clear synced mutations:', error);
    }
  }

  // Fallback methods using localStorage
  private fallbackToLocalStorage(type: string, data: any[]): void {
    try {
      localStorage.setItem(`offline_${type}`, JSON.stringify(data));
    } catch (error) {
      console.error('Failed to fallback to localStorage:', error);
    }
  }

  private getFromLocalStorage<T>(type: string): T[] {
    try {
      const data = localStorage.getItem(`offline_${type}`);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('Failed to get from localStorage:', error);
      return [];
    }
  }

  private queueMutationInLocalStorage(mutation: Omit<OfflineData, 'id' | 'synced'>): string {
    try {
      const mutations = this.getPendingMutationsFromLocalStorage();
      const mutationData: OfflineData = {
        ...mutation,
        id: `${mutation.type}_${mutation.timestamp}_${Math.random()}`,
        synced: false,
      };
      mutations.push(mutationData);
      localStorage.setItem('offline_mutations', JSON.stringify(mutations));
      return mutationData.id;
    } catch (error) {
      console.error('Failed to queue mutation in localStorage:', error);
      return '';
    }
  }

  private getPendingMutationsFromLocalStorage(): OfflineData[] {
    try {
      const data = localStorage.getItem('offline_mutations');
      const mutations = data ? JSON.parse(data) : [];
      return mutations.filter((m: OfflineData) => !m.synced);
    } catch (error) {
      return [];
    }
  }

  private markMutationSyncedInLocalStorage(mutationId: string): void {
    try {
      const mutations = this.getPendingMutationsFromLocalStorage();
      const mutation = mutations.find((m: OfflineData) => m.id === mutationId);
      if (mutation) {
        mutation.synced = true;
        localStorage.setItem('offline_mutations', JSON.stringify(mutations));
      }
    } catch (error) {
      console.error('Failed to mark mutation as synced in localStorage:', error);
    }
  }

  // Get stats
  async getStats(): Promise<{
    pendingMutations: number;
    totalCustomers: number;
    totalBookings: number;
  }> {
    try {
      const pendingMutations = await this.getPendingMutations();
      const customers = await this.getData('customers');
      const bookings = await this.getData('bookings');

      return {
        pendingMutations: pendingMutations.length,
        totalCustomers: customers.length,
        totalBookings: bookings.length,
      };
    } catch (error) {
      return {
        pendingMutations: 0,
        totalCustomers: 0,
        totalBookings: 0,
      };
    }
  }
}

export const offlineStorage = new OfflineStorageService();


